import { SelectColor } from "@/components/elements/SelectColor";
import { Table } from "@/components/elements/Table";
import { LocalTableSorting } from "@/components/elements/Table/LocalTableSorting";
import { DATE_FORMAT } from "@/constants/datetime";
import { SORTING } from "@/constants/sorting";
import { EMPTY_STR } from "@/constants/string";
import {
  ALL_COLUMN_IDS,
  DEFAULT_VISIBLE_COLUMN_IDS,
  MATCHING_RESULTS_COLUMNS,
  METRIC_CONFIG,
  TMatchingResultsColumnDef,
  TMatchingResultsColumnId,
  resolveColumnLabel,
} from "@/features/dashboard/constants/matching-results-columns";
import { MATCHING_RESULTS_SORT_FIELDS, TMatchingResultsItem, TMatchingResultsMetric, TMatchingResultsSortBy, TMatchingResultsSortDir } from "@/features/dashboard/types/matching-results";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { CASE_MATCH_STATUS_COLOR, CaseMatchStatusOptions } from "@/types/enum";
import { getDynamicRoute } from "@/utils/url";
import { truncateWithEllipsis } from "@/utils/string";
import { Typography } from "@mui/material";
import dayjs from "dayjs";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";

type TMatchingResultsTableProps = {
  items: TMatchingResultsItem[];
  isLoading?: boolean;
  metric: TMatchingResultsMetric;
  sortBy: TMatchingResultsSortBy;
  sortDir: TMatchingResultsSortDir;
  onSortChange: (next: { sortBy: TMatchingResultsSortBy; sortDir: TMatchingResultsSortDir } | null) => void;
  visibleColumnIds?: TMatchingResultsColumnId[];
};

// Serialize external sort state (lowercase asc/desc) to the `name|DIR` format
// LocalTableSorting expects (uppercase ASC/DESC - matches SORTING constant).
const sortKeyOf = (sortBy: TMatchingResultsSortBy, sortDir: TMatchingResultsSortDir) =>
  `${sortBy}|${sortDir === "asc" ? SORTING.ASC : SORTING.DESC}`;

/**
 * Parse LocalTableSorting's emitted string (uppercase) back into structured state.
 * BE expects lowercase asc/desc, so we convert here.
 * Returns null when user clears sort (empty string).
 */
const parseSortKey = (raw: string): { sortBy: TMatchingResultsSortBy; sortDir: TMatchingResultsSortDir } | null => {
  if (!raw) return null;
  const [name, dir] = raw.split("|");
  if (dir !== SORTING.ASC && dir !== SORTING.DESC) return null;
  if (!(MATCHING_RESULTS_SORT_FIELDS as readonly string[]).includes(name)) return null;
  return { sortBy: name as TMatchingResultsSortBy, sortDir: dir === SORTING.ASC ? "asc" : "desc" };
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return EMPTY_STR.TEXT;
  const d = dayjs(iso);
  return d.isValid() ? d.format(DATE_FORMAT) : EMPTY_STR.TEXT;
};

const MAX_SHOW_TEXT_LENGTH = 15;
const TextCell: FC<{ text: string }> = ({ text }) => <Typography variant="body2">{truncateWithEllipsis(text, MAX_SHOW_TEXT_LENGTH)}</Typography>;

// Cell renderers - one per column id. Keeps `getColumnDef` declarative.
const renderCell = (col: TMatchingResultsColumnDef, row: TMatchingResultsItem, metric: TMatchingResultsMetric) => {
  switch (col.id) {
    case "status":
      return (
        <SelectColor
          nonControl
          options={CaseMatchStatusOptions}
          value={row.status}
          size="md"
          clearable={false}
          optionColors={CASE_MATCH_STATUS_COLOR}
          readonly
          hideIcon
        />
      );
    case "talentName":
      return <TextCell text={row.talent?.fullName ?? EMPTY_STR.TEXT} />;
    case "caseName":
      return <TextCell text={row.case?.name ?? EMPTY_STR.TEXT} />;
    case "jobName":
      return <TextCell text={row.job?.jobName ?? EMPTY_STR.TEXT} />;
    case "companySpecificIndustry":
      return <TextCell text={row.talent?.companySpecificIndustry ?? EMPTY_STR.TEXT} />;
    case "companyName":
      return <TextCell text={row.company?.companyName ?? EMPTY_STR.TEXT} />;
    case "metricDate": {
      const field = METRIC_CONFIG[metric].dateField;
      return <TextCell text={formatDate(row[field] as string | null)} />;
    }
    case "caseOwner":
      return <TextCell text={row.caseOwner?.fullName ?? EMPTY_STR.TEXT} />;
    case "jobRecruiter":
      return <TextCell text={row.job?.recruiterName ?? EMPTY_STR.TEXT} />;
    case "talentPersonInCharge":
      return <TextCell text={row.talent?.personInChargeName ?? EMPTY_STR.TEXT} />;
    default:
      return EMPTY_STR.TEXT;
  }
};

export const MatchingResultsTable: FC<TMatchingResultsTableProps> = ({
  items,
  isLoading,
  metric,
  sortBy,
  sortDir,
  onSortChange,
  visibleColumnIds = DEFAULT_VISIBLE_COLUMN_IDS,
}) => {
  const activeSortKey = sortKeyOf(sortBy, sortDir);

  const handleSort = (sortKeyRaw: string) => {
    onSortChange(parseSortKey(sortKeyRaw));
  };

  const columns = useMemo<MRT_ColumnDef<TMatchingResultsItem>[]>(() => {
    return MATCHING_RESULTS_COLUMNS.filter((col) => visibleColumnIds.includes(col.id)).map((col) => {
      const label = resolveColumnLabel(col, metric);

      return {
        id: col.id,
        header: label,
        size: col.width,
        Cell: ({ row }) => renderCell(col, row.original, metric),
        Header: col.sortable && col.sortBy ? (
          <LocalTableSorting name={col.sortBy} currentSort={activeSortKey} onSort={handleSort}>
            {label}
          </LocalTableSorting>
        ) : (
          label
        ),
      };
    });
  }, [metric, visibleColumnIds, activeSortKey]);

  const handleRowClick = (row: TMatchingResultsItem) => {
    if (!row.talent?.id) return;
    const url = getDynamicRoute(AppRoutes.talentDetail, { talentId: String(row.talent.id) });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Pin column order to the canonical list; otherwise MRT appends a re-shown column to the end
  // when the table was mounted with that column already hidden.
  const { table } = useTable({
    rows: items,
    columns,
    onClickRow: handleRowClick,
    state: { columnOrder: ALL_COLUMN_IDS },
  });

  return <Table table={table} isLoading={isLoading} headRowHeight={37} bodyRowHeight={42} maxHeight="55vh" />;
};
